import React, { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { API_URL } from "../../constant";
import "./CaptainTCCard.css";
import fivePoundCardImg from "../../images/captain-tc/5PoundCard.jpg";
import copperImg from "../../images/captain-tc/copper.jpg";
import silverImg from "../../images/captain-tc/silver.png";
import bronzeImg from "../../images/captain-tc/bronze.png";
import goldImg from "../../images/captain-tc/gold.png";
import tRewardsImg from "../../images/captain-tc/t-rewards.png";

// Helper to determine card image
const getCardImage = (card) => {
  if (card?.image) {
    return card.image.startsWith("http")
      ? card.image
      : `${API_URL}/storage/${card.image}`;
  }
  const name = (card?.name || "").toLowerCase();
  if (name.includes("copper")) return copperImg;
  if (name.includes("silver")) return silverImg;
  if (name.includes("bronze")) return bronzeImg;
  if (name.includes("gold")) return goldImg;
  if (name.includes("t reward") || name.includes("t card") || name.includes("(t)")) return tRewardsImg;
  return fivePoundCardImg;
};

// Helper to format card title
const getCardTitle = (card) => {
  if (!card || !card.purchased) return "Captain TC Reward Card";
  const name = card.name || "";
  if (/copper/i.test(name)) return "Captain TC Copper Card";
  if (/silver/i.test(name)) return "Captain TC Silver Card";
  if (/bronze/i.test(name)) return "Captain TC Bronze Card";
  if (/gold/i.test(name)) return "Captain TC Gold Card";
  if (/t reward|t card|\(t\)/i.test(name)) return "Captain TC T Card";
  if (name.toLowerCase().startsWith("captain tc")) return name;
  return `Captain TC ${name} Card`;
};

// Helper to get milestone reward price per 250 TopCoin milestone
const getMilestonePrice = (card) => {
  if (!card) return 1.25;
  const name = (card.name || "").toLowerCase();
  if (name.includes("copper")) return 1.25;
  if (name.includes("silver")) return 2.50;
  if (name.includes("bronze")) return 5.00;
  if (name.includes("gold")) return 12.50;
  if (name.includes("t card") || name.includes("t reward") || name.includes("(t)")) return 22.15;

  const priceVal = parseFloat(String(card.price || "").replace(/[^0-9.]/g, ""));
  if (priceVal === 2.5) return 1.25;
  if (priceVal === 5.0) return 2.50;
  if (priceVal === 10.0) return 5.00;
  if (priceVal === 25.0) return 12.50;

  return 1.25;
};

function CaptainTCCard() {
  const outletContext = useOutletContext() || {};
  const { userData } = outletContext;

  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);

  // Promo wallet balances
  const [extraCardBalance, setExtraCardBalance] = useState(0);
  const [bonusCardBalance, setBonusCardBalance] = useState(0);
  const [unclaimedMilestones, setUnclaimedMilestones] = useState(0);
  const [claimedMilestones, setClaimedMilestones] = useState(0);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);

  // History state
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [selectedSbcRef, setSelectedSbcRef] = useState(null);

  // Full view modal state
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Milestone table full rows toggle
  const [showAllMilestones, setShowAllMilestones] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("auth_token");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }, []);

  const fetchCardStatus = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      if (!token) return;

      const response = await fetch(`${API_URL}/api/customer/captain-tc-cards`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.cards) {
          setCards(data.cards || []);
        }
        if (data.promo_wallet) {
          const promo = data.promo_wallet;
          const extra = promo.extra_card_balance || 0;
          setExtraCardBalance(extra);
          setBonusCardBalance(promo.bonus_card_balance || 0);
          const unclaimed = promo.unclaimed_milestones || 0;
          setUnclaimedMilestones(unclaimed);
          const claimed =
            promo.claimed_milestones !== undefined
              ? promo.claimed_milestones
              : Math.max(0, Math.floor(extra / 250) - unclaimed);
          setClaimedMilestones(claimed);
          setHasPendingRequest(promo.has_pending_request || false);
        }
      }
    } catch (err) {
      console.warn("Captain TC Cards API not available:", err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const handleClaimCashback = async () => {
    if (hasPendingRequest || unclaimedMilestones <= 0) return;

    try {
      setClaimLoading(true);
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_URL}/api/customer/captain-tc-cards/claim-cashback`,
        {
          method: "POST",
          headers: getAuthHeaders(),
        },
      );

      const data = await response.json();
      if (response.ok && data.success) {
        alert(data.message || "Cash back claim submitted successfully.");
        setHasPendingRequest(true);
        setUnclaimedMilestones(0);
        fetchCardStatus();
      } else {
        alert(data.message || "Failed to submit claim.");
      }
    } catch (err) {
      console.error("Error claiming cash back:", err);
      alert("An error occurred during submission.");
    } finally {
      setClaimLoading(false);
    }
  };

  const fetchHistory = useCallback(
    async (page = 1, limit = 5) => {
      try {
        setHistoryLoading(true);
        const token = localStorage.getItem("auth_token");
        if (!token) return;

        const response = await fetch(
          `${API_URL}/api/customer/captain-tc-cards/history?page=${page}&per_page=${limit}`,
          {
            headers: getAuthHeaders(),
          },
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.history) {
            setHistory(data.history.data || []);
            setHistoryPage(data.history.current_page || 1);
            setHistoryTotalPages(data.history.last_page || 1);
          }
        }
      } catch (err) {
        console.warn("Captain TC Cards history API error:", err.message);
      } finally {
        setHistoryLoading(false);
      }
    },
    [getAuthHeaders],
  );

  const toggleHistoryView = () => {
    const newShowFull = !showFullHistory;
    setShowFullHistory(newShowFull);
    fetchHistory(1, newShowFull ? 10 : 5);
  };

  useEffect(() => {
    fetchCardStatus();
    fetchHistory(1, 5);
  }, [fetchCardStatus, fetchHistory]);

  // Handle escape key to close image modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsImageModalOpen(false);
      }
    };
    if (isImageModalOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isImageModalOpen]);

  if (loading) {
    return (
      <div className="captain-tc-wrapper">
        <div className="captain-tc-loading">
          <div className="captain-tc-spinner"></div>
        </div>
      </div>
    );
  }

  // Active / Purchased Card determination
  const purchasedCards = cards.filter((c) => c.purchased);
  const hasPurchasedCard = purchasedCards.length > 0;
  const activeCard = hasPurchasedCard
    ? (purchasedCards.find((c) => c.id === selectedCardId) ||
       purchasedCards.find((c) => !c.is_expired) ||
       purchasedCards[0] ||
       null)
    : null;

  const cardTitle = getCardTitle(activeCard);
  const cardImgSrc = hasPurchasedCard && activeCard ? getCardImage(activeCard) : null;
  const milestonePrice = hasPurchasedCard && activeCard ? getMilestonePrice(activeCard) : null;

  // Table 1 Progress calculations
  const targetTC = 5000;
  const earnedTC = (activeCard?.extra_card_balance > 0)
    ? activeCard.extra_card_balance
    : extraCardBalance;
  const redeemedTC = claimedMilestones * 250;
  const balanceTCLeft = Math.max(0, earnedTC - redeemedTC);
  const earnableTC = Math.max(0, targetTC - earnedTC);

  // Table 2 Milestones summary calculation
  const withdrawableMilestone = Math.min(5000, Math.floor(earnedTC / 250) * 250);
  const withdrawableCount = Math.min(20, Math.floor(earnedTC / 250));
  const progressiveMilestoneRows = Math.min(20, withdrawableCount + 1);
  const visibleMilestoneRows = showAllMilestones ? 20 : progressiveMilestoneRows;
  const totalWithdrawableGBP = (milestonePrice !== null) ? (withdrawableCount * milestonePrice).toFixed(2) : "—";
  const totalTopcoinRewardGBP = hasPurchasedCard ? (withdrawableMilestone * 0.005).toFixed(2) : "—";

  return (
    <div className="captain-tc-wrapper">
      {/* Multi-card selector tabs if more than one purchased card */}
      {purchasedCards.length > 1 && (
        <div className="captain-tc-tabs-bar">
          {/* <span className="tabs-label">Your Cards:</span> */}
          {purchasedCards.map((c) => (
            <button
              key={c.id}
              className={`captain-tc-tab-btn ${c.id === (activeCard?.id || purchasedCards[0].id) ? "active" : ""}`}
              onClick={() => setSelectedCardId(c.id)}
            >
              {getCardTitle(c)} ({c.serial || `#${c.id}`})
            </button>
          ))}
        </div>
      )}

      {/* Top Header Row matching user mockup */}
      <div className={`captain-tc-header-row ${!hasPurchasedCard ? "no-card-header" : ""}`}>
        <div className="captain-tc-header-title-box">
          <h2 className="captain-tc-main-title">{cardTitle}</h2>
        </div>

        {hasPurchasedCard && cardImgSrc && (
          <div
            className="captain-tc-header-img-box"
            onClick={() => setIsImageModalOpen(true)}
            title="Click to view full card"
            style={{ cursor: "pointer" }}
          >
            <img
              src={cardImgSrc}
              alt={cardTitle}
              className="captain-tc-header-thumb"
              height={110}
              style={{
                height: "110px",
                maxHeight: "110px",
                width: "auto",
                maxWidth: "90px",
                objectFit: "contain",
                borderRadius: "8px",
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.12)",
                border: "1px solid #cbd5e1",
                display: "block",
                cursor: "pointer",
              }}
            />
          </div>
        )}

        {hasPurchasedCard ? (
          <div className="captain-tc-header-serial-box">
            <span className="serial-label">SERIAL</span>
            <span className="serial-value">{activeCard?.serial || "—"}</span>
            {activeCard?.is_expired && (
              <span className="badge-expired" style={{ marginLeft: "10px" }}>
                Expired
              </span>
            )}
          </div>
        ) : (
          <div className="captain-tc-no-card-badge">
            <i className="fa fa-info-circle" style={{ marginRight: "6px" }}></i>
            No Reward Card Purchased
          </div>
        )}
      </div>

      {/* Full View Card Image Modal */}
      {isImageModalOpen && (
        <div
          className="captain-tc-image-modal-overlay"
          onClick={() => setIsImageModalOpen(false)}
        >
          <div
            className="captain-tc-image-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="captain-tc-modal-close-btn"
              onClick={() => setIsImageModalOpen(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <img
              src={cardImgSrc}
              alt={cardTitle}
              className="captain-tc-full-card-img"
            />
            <div className="captain-tc-modal-footer">
              <span className="modal-card-title">{cardTitle}</span>
              {activeCard?.serial && (
                <span className="modal-card-serial">
                  SERIAL: {activeCard.serial}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Captain TC Reward Card Purchase Details Table */}
      <div className="captain-tc-card-box">
        <div className="reward-promo-header">
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#1e293b" }}>
            Captain TC Reward Card Purchase Details
          </h3>
          <button
            className="view-history-btn"
            onClick={toggleHistoryView}
          >
            <i className={`fa ${showFullHistory ? "fa-chevron-up" : "fa-history"}`}></i>{" "}
            {showFullHistory ? "Show Less" : "View Full History"}
          </button>
        </div>

        <div className="captain-tc-table-wrap">
          <table className="captain-tc-grid-table table-one">
            <thead>
              <tr>
                <th>NO</th>
                <th>Target</th>
                <th>Captain TC Reward TopCoin Earned</th>
                <th>TopCoin Redeem</th>
                <th>Balance TC Left</th>
                <th>Earnable TC</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const cardList =
                  purchasedCards.length > 0
                    ? purchasedCards
                    : [{ id: 1, name: activeCard?.name || "Captain TC Reward Card" }];
                const pageSize = showFullHistory ? 10 : 5;
                const rows = cardList.slice((historyPage - 1) * pageSize, historyPage * pageSize);

                return rows.map((c, index) => {
                  const cardEarned =
                    c.extra_card_balance > 0
                      ? c.extra_card_balance
                      : cardList.length === 1
                        ? earnedTC
                        : 0;
                  const cardRedeemed =
                    (c.claimed_milestones !== undefined
                      ? c.claimed_milestones
                      : cardList.length === 1
                        ? claimedMilestones
                        : 0) * 250;
                  const cardBalanceLeft = Math.max(0, cardEarned - cardRedeemed);
                  const cardEarnable = Math.max(0, targetTC - cardEarned);
                  const rowNum = (historyPage - 1) * pageSize + index + 1;

                  return (
                    <tr
                      key={c.id || index}
                      onClick={() => c.id && setSelectedCardId(c.id)}
                      style={{ cursor: purchasedCards.length > 1 ? "pointer" : "default" }}
                    >
                      <td className="text-center font-bold">{rowNum}</td>
                      <td className="text-center font-bold">{targetTC.toLocaleString()}</td>
                      <td className="text-center font-bold text-blue">{cardEarned.toLocaleString()}</td>
                      <td className="text-center font-bold">{cardRedeemed.toLocaleString()}</td>
                      <td className="text-center font-bold">{cardBalanceLeft.toLocaleString()}</td>
                      <td className="text-center font-bold">{cardEarnable.toLocaleString()}</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>

        {/* Pagination controls if more than 1 page */}
        {showFullHistory && purchasedCards.length > 10 && (
          <div
            className="pagination-container"
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "16px",
              gap: "10px",
            }}
          >
            <button
              disabled={historyPage === 1}
              onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
              className="pagination-btn"
              style={{
                padding: "6px 14px",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                background: historyPage === 1 ? "#f1f5f9" : "#ffffff",
                color: historyPage === 1 ? "#94a3b8" : "#334155",
                cursor: historyPage === 1 ? "not-allowed" : "pointer",
                fontWeight: "600",
                fontSize: "13px",
              }}
            >
              Prev
            </button>
            <span
              style={{
                alignSelf: "center",
                fontSize: "13px",
                fontWeight: "600",
                color: "#475569",
              }}
            >
              Page {historyPage} of {Math.ceil(purchasedCards.length / 10)}
            </span>
            <button
              disabled={historyPage === Math.ceil(purchasedCards.length / 10)}
              onClick={() =>
                setHistoryPage((p) => Math.min(Math.ceil(purchasedCards.length / 10), p + 1))
              }
              className="pagination-btn"
              style={{
                padding: "6px 14px",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                background:
                  historyPage === Math.ceil(purchasedCards.length / 10)
                    ? "#f1f5f9"
                    : "#ffffff",
                color:
                  historyPage === Math.ceil(purchasedCards.length / 10)
                    ? "#94a3b8"
                    : "#334155",
                cursor:
                  historyPage === Math.ceil(purchasedCards.length / 10)
                    ? "not-allowed"
                    : "pointer",
                fontWeight: "600",
                fontSize: "13px",
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Table 2: Milestones Progression Table */}
      <div className="captain-tc-card-box">
        <div className="reward-promo-header">
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#1e293b" }}>
            Captain TC Card Milestones
          </h3>
          <button
            className="view-history-btn"
            onClick={() => setShowAllMilestones(!showAllMilestones)}
          >
            <i className={`fa ${showAllMilestones ? "fa-chevron-up" : "fa-list"}`}></i>{" "}
            {showAllMilestones ? "Show Less" : "View All Milestones"}
          </button>
        </div>

        <div className="captain-tc-table-wrap">
          <table className="captain-tc-grid-table table-two">
            <thead>
              <tr>
                <th style={{ width: "80px" }}>No</th>
                <th>TopCoin MileStone</th>
                <th>Price</th>
                <th style={{ width: "160px" }}>Withdrawable</th>
                <th style={{ width: "160px" }}>Withdrawn</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: visibleMilestoneRows }, (_, idx) => {
                const milestoneNum = idx + 1;
                const milestoneTC = milestoneNum * 250;
                const isWithdrawable = earnedTC >= milestoneTC;
                const isWithdrawn = redeemedTC >= milestoneTC;

                return (
                  <tr key={milestoneNum}>
                    <td className="text-center">{milestoneNum}</td>
                    <td className="text-center font-semibold">{milestoneTC.toLocaleString()}</td>
                    <td className="text-center font-semibold">
                      {hasPurchasedCard && milestonePrice !== null ? milestonePrice.toFixed(2) : "—"}
                    </td>
                    <td className={`text-center ${isWithdrawable ? "cell-withdrawable-reached" : ""}`}>
                      {isWithdrawable ? "✔" : ""}
                    </td>
                    <td className="text-center cell-withdrawn">
                      {isWithdrawn ? "✔" : ""}
                    </td>
                  </tr>
                );
              })}
              {/* Total Row when all 20 milestones reached */}
              {visibleMilestoneRows === 20 && (
                <tr className="table-total-row">
                  <td className="text-center font-bold">Total</td>
                  <td className="text-center font-bold">5,000</td>
                  <td className="text-center font-bold">
                    {hasPurchasedCard && milestonePrice !== null ? (20 * milestonePrice).toFixed(2) : "—"}
                  </td>
                  <td></td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Withdrawable Summary Box below Table 2 */}
        <div className="captain-tc-summary-box">
          <table className="captain-tc-summary-table">
            <tbody>
              <tr>
                <td className="summary-main-label" rowSpan="2">
                  Withdrawable
                </td>
                <td className="summary-desc-label">
                  {hasPurchasedCard
                    ? `${cardTitle} ${withdrawableMilestone.toLocaleString()} Mile stone`
                    : "No Card Purchased"}
                </td>
                <td className="summary-amount-val">
                  {hasPurchasedCard ? totalWithdrawableGBP : "—"}
                </td>
              </tr>
              <tr>
                <td className="summary-desc-label">
                  Topcoin TC Reward TopCoin
                </td>
                <td className="summary-amount-val">
                  {hasPurchasedCard ? totalTopcoinRewardGBP : "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Claim Cash Back Section */}
      <div className="captain-tc-card-box captain-tc-claim-card">
        <div className="captain-tc-claim-header">
          <h3 style={{ margin: "0 0 6px 0", color: "#1e293b", fontSize: "18px", fontWeight: "700" }}>
            Claim Cash Back
          </h3>
          <p style={{ fontSize: "14px", color: "#64748b", margin: 0, lineHeight: "1.5" }}>
            Rules: You need at least 250 TopCoins in extra card balance to claim. Each card covers 20 claims. Once requested, the Account Admin will review and approve your bonus card balance.
          </p>
        </div>
        <div style={{ marginTop: "16px" }}>
          <button
            onClick={handleClaimCashback}
            disabled={claimLoading || hasPendingRequest || unclaimedMilestones <= 0}
            className="claim-cashback-action-btn"
          >
            {claimLoading
              ? "Submitting..."
              : hasPendingRequest
                ? "Cash Back Requested (Pending Approval)"
                : `Claim Cash Back (${unclaimedMilestones} available)`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CaptainTCCard;
